import { i18nNsFactory } from '@atlas-frontend/i18n';
import type { MenuProps } from 'antd/es/menu';
import { FC, MouseEventHandler } from 'react';
import { Button, ButtonProps, Dropdown, Flex } from 'antd';
import { Tooltip } from './Tooltip';
import { SizeType } from 'antd/es/config-provider/SizeContext';
import * as Icon from 'react-feather';
import translations from './AddRemoveButtons.translations.json';

const { addTranslations, useTranslation } = i18nNsFactory('add-remove-buttons');
addTranslations(translations);

interface AddRemoveButtonsBaseProps {
  id: string;
  rowName?: string;
  isAddDisabled?: boolean | string;
  size?: SizeType;
}

export type AddRemoveButtonsProps = AddRemoveButtonsBaseProps &
  (
    | {
        onClickRemove?: MouseEventHandler<HTMLElement>;
        onClickAdd?: MouseEventHandler<HTMLElement>;
      }
    | {
        removeMenu?: MenuProps;
        onClickAdd?: MouseEventHandler<HTMLElement>;
      }
    | {
        onClickRemove?: MouseEventHandler<HTMLElement>;
        addMenu?: MenuProps;
      }
    | {
        removeMenu?: MenuProps;
        addMenu?: MenuProps;
      }
  );

export const AddRemoveButtons: FC<AddRemoveButtonsProps> = (props) => {
  const { id, rowName = '', isAddDisabled, size = 'small', ...rest } = props;
  const { t } = useTranslation();

  return (
    <Flex gap="small">
      {(('onClickAdd' in rest && rest.onClickAdd) ||
        ('addMenu' in rest && rest.addMenu)) && (
        <Tooltip
          title={
            typeof isAddDisabled === 'string'
              ? isAddDisabled
              : t('add', { rowName })
          }
        >
          {'addMenu' in rest && rest.addMenu ? (
            <Dropdown menu={rest.addMenu} trigger={['click']}>
              <AddRemoveButton
                id={`${id}-add`}
                size={size}
                type="add"
                isDisabled={Boolean(isAddDisabled)}
              />
            </Dropdown>
          ) : (
            'onClickAdd' in rest &&
            rest.onClickAdd && (
              <AddRemoveButton
                id={`${id}-add`}
                size={size}
                type="add"
                onClick={rest.onClickAdd}
                isDisabled={Boolean(isAddDisabled)}
              />
            )
          )}
        </Tooltip>
      )}
      {(('onClickRemove' in rest && rest.onClickRemove) ||
        ('removeMenu' in rest && rest.removeMenu)) && (
        <Tooltip title={t('remove', { rowName })}>
          {'removeMenu' in rest && rest.removeMenu ? (
            <Dropdown menu={rest.removeMenu} trigger={['click']}>
              <AddRemoveButton id={`${id}-remove`} size={size} type="remove" />
            </Dropdown>
          ) : (
            'onClickRemove' in rest &&
            rest.onClickRemove && (
              <AddRemoveButton
                id={`${id}-remove`}
                size={size}
                type="remove"
                onClick={rest.onClickRemove}
              />
            )
          )}
        </Tooltip>
      )}
    </Flex>
  );
};

export interface AddRemoveButtonProps extends Omit<ButtonProps, 'type'> {
  type: 'add' | 'remove';
  size: SizeType;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  isDisabled?: boolean;
}

export const AddRemoveButton: FC<AddRemoveButtonProps> = ({
  id,
  type,
  size,
  onClick,
  isDisabled,
  ...rest
}) => (
  <Button
    id={id}
    size={size}
    shape="circle"
    icon={
      type === 'add' ? (
        <Icon.Plus width="1em" height="1em" />
      ) : (
        <Icon.Minus width="1em" height="1em" />
      )
    }
    onClick={onClick}
    disabled={isDisabled}
    {...rest}
  />
);
